import React from "react";
import { Container, Typography, Box, List, ListItem } from "@mui/material";
import { styles } from "./stylesPrivacyPolicy";
import theme from "@/theme";

const PrivacyPolicy: React.FC = () => {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sectionLinks = (text: string) =>
    text.split(/(Section \d+(?:\.\d+)?)/g).map((part, i) => {
      const match = part.match(/^Section (\d+)(?:\.(\d+))?$/);
      if (!match) return part;
      const id = match[2] ? `section-${match[1]}-${match[2]}` : `section-${match[1]}`;
      return (
        <Box
          component="span"
          key={i}
          onClick={() => scrollToSection(id)}
          sx={{ ...styles.link, color: theme.palette.custom.mainBlue }}
        >
          {part}
        </Box>
      );
    });

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom sx={styles.mainTitle}>
        Wethemrkt Privacy Policy
      </Typography>

      <Box mb={3}>
        <Typography sx={styles.highlightedText}>
          Effective date: 13 July 2026 · Last updated: 13 July 2026
        </Typography>
      </Box>

      {/* 60 seconds */}
      <Box mb={4}>
        <Typography sx={styles.sectionTitle}>What This Means in 60 Seconds</Typography>
        <Typography sx={styles.bodyText} mb={2}>
          We know nobody actually enjoys reading a privacy policy, so here’s the plain-English
          version. The full legal detail is below for anyone who wants (or needs) it.
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Who we are:
              </Box>{" "}
              Wethemrkt is a platform where stock investors talk markets, tip each other, and post
              analysis.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                What we collect:
              </Box>{" "}
              Your account info (name, email, username), the posts and comments you make, basic
              device/usage data, and — if you deposit, withdraw, or spend more than $1,000 in a
              month — identity verification (KYC) documents required by law.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Money stuff:
              </Box>{" "}
              Your balance sits in USDC, held in trust for you by our payments partner, Speed
              (tryspeed.com), in a pooled (“omnibus”) wallet. We take a 10% cut on likes, zaps, and
              paid content. Deposits and withdrawals go through Solana or Bitcoin Lightning, and
              Speed — not us — handles the crypto rails and any KYC checks required above the
              $1,000/month threshold.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              We don’t sell your data to advertisers, and we don’t run ads or algorithmic feeds —
              you control your own sort order and who you follow.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              We do share data with service providers who help us run the platform (like Speed for
              payments, and analytics/hosting providers), and we explain exactly who and why below.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Your rights:
              </Box>{" "}
              Depending on where you live, you may have rights to access, correct, delete, or limit
              how we use your information — including specific rights under the Privacy Act 1988
              (Cth) and various US state privacy laws. We explain how to exercise these, and how
              long we’ll take to respond, in detail below.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Kids:
              </Box>{" "}
              Wethemrkt isn’t for anyone under 13 (and in some places, under 16). We don’t knowingly
              collect data from children.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Questions or requests:
              </Box>{" "}
              Email info@wethemrkt.com for anything, privacy-related or otherwise.
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          That’s the gist. Now, the detail — because when money and identity documents are involved,
          detail matters.
        </Typography>
      </Box>

      {/* Table of contents */}
      <Box mb={4}>
        <Typography sx={styles.sectionTitle}>Table of Contents</Typography>
        <List>
          {[
            "Introduction and Scope",
            "Who We Are",
            "Information We Collect",
            "Sensitive Personal Information",
            "How We Use Your Information",
            "How We Share Your Information",
            "Cookies and Tracking Technologies",
            "Overseas Data Transfers",
            "Payments, Wallets, and Speed",
            "Identity Verification (KYC) and Anti-Money Laundering",
            "Data Retention",
            "Data Security and Breach Notification",
            "Your Privacy Rights — Australia",
            "Automated Decision-Making",
            "Your Privacy Rights — United States",
            "Children’s Privacy",
            "Version History",
            "Changes to This Policy",
            "Contact Us and Complaints",
          ].map((item, index) => (
            <ListItem key={item} sx={styles.listItem}>
              <Typography
                onClick={() => scrollToSection(`section-${index + 1}`)}
                sx={{ ...styles.link, color: theme.palette.custom.mainBlue }}
              >
                {item}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* 1 */}
      <Box mb={4}>
        <Typography id="section-1" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          1. Introduction and Scope
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          This Privacy Policy explains how Wethemrkt (“Wethemrkt,” “we,” “us,” or “our”) collects,
          uses, discloses, and protects personal information when you use wethemrkt.com, our mobile
          and web applications, and related services (collectively, the “Platform”).
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          This Policy applies to all users of the Platform, including visitors, registered users,
          free-tier users, and Premium subscribers, regardless of location. Wethemrkt serves users
          globally, and this Policy is designed to meet our obligations under applicable law,
          including the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs), as well
          as applicable US state privacy laws for users located in those states.
        </Typography>
        <Typography sx={styles.bodyText}>
          By using the Platform, you acknowledge that you have read and understood this Policy. If
          you do not agree with our practices, please do not use the Platform.
        </Typography>
      </Box>

      {/* 2 */}
      <Box mb={4}>
        <Typography id="section-2" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          2. Who We Are
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          Wethemrkt is a platform built for stock investors to discuss markets, share tips, post
          analysis, and support each other’s work through likes, zaps, and paid content. We describe
          ourselves — with a wink — as “OnlyFans for stock investors”: no ads, no algorithmic
          manipulation, and you control how your feed is sorted.
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          Business name: Wethemrkt · Registered address: [ADDRESS PLACEHOLDER] · Contact:
          info@wethemrkt.com
        </Typography>
        <Typography sx={styles.bodyText}>
          For the purposes of the Privacy Act 1988 (Cth) and the Australian Privacy Principles,
          Wethemrkt is the entity responsible for handling your personal information on the Platform
          (the “APP entity”) for users to whom that law applies. For the purposes of US state
          privacy laws, Wethemrkt is the “business” or “controller” that determines the purposes and
          means of processing your personal information.
        </Typography>
      </Box>

      {/* 3 */}
      <Box mb={4}>
        <Typography id="section-3" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          3. Information We Collect
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          We collect the following categories of information:
        </Typography>

        <Typography sx={styles.listItemTitle}>3.1 Information You Give Us</Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Account information:
              </Box>{" "}
              name, username, email address, password (hashed), profile photo, bio.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Content you post:
              </Box>{" "}
              posts across our categories (Stocks — including cashtags and technical/fundamental
              tags, Economics, Personal, Articles), comments, replies, likes, zaps, and any media
              you upload.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Communications:
              </Box>{" "}
              messages you send to our support team or through in-platform contact tools.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Payment and wallet-related information:
              </Box>{" "}
              {sectionLinks("as described in detail in Section 9 and Section 10.")}
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Subscription information:
              </Box>{" "}
              if you subscribe to Premium ($5/month at current limited-time launch pricing), we (or
              our payment processor) collect the billing information needed to process that
              subscription.
            </Typography>
          </ListItem>
        </List>

        <Typography sx={styles.listItemTitle} mt={2}>
          3.2 Information We Collect Automatically
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Device and usage data:
              </Box>{" "}
              IP address, browser type, device identifiers, operating system, pages viewed,
              referring URLs, timestamps, and general usage patterns (e.g., which post categories
              you engage with, follow/unfollow activity).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Approximate location:
              </Box>{" "}
              derived from IP address, for security, fraud prevention, and localization purposes.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Cookies and similar technologies:
              </Box>{" "}
              {sectionLinks("see Section 7.")}
            </Typography>
          </ListItem>
        </List>

        <Typography sx={styles.listItemTitle} mt={2}>
          3.3 Information from Third Parties
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Payment processing data:
              </Box>{" "}
              {sectionLinks(
                "from Speed (tryspeed.com), our payments backend, including transaction confirmations and, where applicable, verification status (see Section 9 and Section 10)."
              )}
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Analytics providers:
              </Box>{" "}
              aggregated or de-identified usage statistics from analytics tools we use to understand
              Platform performance.
            </Typography>
          </ListItem>
        </List>
      </Box>

      {/* 4 */}
      <Box mb={4}>
        <Typography id="section-4" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          4. Sensitive Personal Information
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          Some of the information we collect qualifies as Sensitive Personal Information (SPI) under
          the California Consumer Privacy Act as amended by the California Privacy Rights Act
          (CCPA/CPRA), and/or as “sensitive information” under the Privacy Act 1988 (Cth). We are
          transparent about this because it deserves a dedicated section, not a footnote.
        </Typography>
        <Typography sx={styles.bodyText} mb={1}>
          The categories of SPI we may collect include:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Financial account information
              </Box>{" "}
              — Examples: wallet balances, transaction history, deposit/withdrawal records, payment
              method details. Why we collect it: to operate the Platform’s tipping, zaps, and
              paid-content features, and to meet financial recordkeeping obligations.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Government identity documents (KYC)
              </Box>{" "}
              — Examples: passport, driver’s licence, or national ID, and associated verification
              data. Why we collect it: required once you exceed the $1,000/month deposit or
              withdrawal threshold, to comply with AML/CTF and know-your-customer laws (see Section
              10).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Precise geolocation
              </Box>{" "}
              — Examples: GPS-level or similarly precise location data, where separately enabled.
              Why we collect it: we do not currently collect precise geolocation by default; if any
              feature requiring it is introduced, we will update this Policy and seek any necessary
              consent before collection.
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          We do not use or disclose SPI for purposes beyond what is reasonably necessary to provide
          the services you request, ensure security and fraud prevention, and comply with legal
          obligations (including financial services and AML/CTF law). We do not use SPI to infer
          characteristics about you for advertising purposes, and we do not sell SPI.
        </Typography>
        <Typography sx={styles.bodyText}>
          {sectionLinks(
            "You have a right to limit our use of SPI — see Section 15.4 for how to exercise this right and our response timeline."
          )}
        </Typography>
      </Box>

      {/* 5 */}
      <Box mb={4}>
        <Typography id="section-5" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          5. How We Use Your Information
        </Typography>
        <Typography sx={styles.bodyText} mb={1}>
          We use personal information to:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Create and administer your account, and authenticate you when you log in.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Enable core Platform functionality: posting, following, liking, zapping, tipping, paid
              content, and Premium features such as filtered feeds and trending views.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Process payments, subscriptions, and the 10% platform transaction fee on likes, zaps,
              and paid content, via Speed.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Verify your identity where required by law (KYC/AML obligations above the $1,000/month
              threshold).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Provide customer support and respond to your enquiries.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Maintain the security, integrity, and stability of the Platform, including detecting
              and preventing fraud, abuse, and unauthorised access.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Comply with applicable law, regulation, legal process, or enforceable governmental
              request, including financial services, tax, and AML/CTF obligations.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Communicate with you about your account, transactions, and material changes to our
              services or this Policy.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Understand aggregate usage trends to improve the Platform (we do not use this to power
              advertising or algorithmic feed manipulation — we don’t have algorithmic feeds).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Administer Alpha Awards (coming soon) — our planned weekly cash-prize draws for
              Premium members, funded by 15% of platform revenue — including eligibility
              verification and prize distribution once launched.
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          We do not use your personal information to serve you third-party advertisements, and the
          Platform does not use engagement-optimising algorithmic feeds. Feed and trending sort
          order are user-controlled.
        </Typography>
      </Box>

      {/* 6 */}
      <Box mb={4}>
        <Typography id="section-6" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          6. How We Share Your Information
        </Typography>
        <Typography sx={styles.bodyText} mb={1}>
          We do not sell your personal information to third parties for money, and we do not share
          it for cross-context behavioural advertising. We disclose personal information only in the
          following circumstances:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Payment processing:
              </Box>{" "}
              {sectionLinks(
                "with Speed (tryspeed.com) to process deposits, withdrawals, subscriptions, and platform fees — see Section 9."
              )}
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Service providers:
              </Box>{" "}
              with vendors who perform services on our behalf (e.g., cloud hosting, analytics,
              customer support tooling, email delivery), under contractual confidentiality and data
              protection obligations.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Legal and safety reasons:
              </Box>{" "}
              where required by law, regulation, court order, or to protect the rights, property, or
              safety of Wethemrkt, our users, or the public, including reporting obligations under
              AML/CTF law.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Business transfers:
              </Box>{" "}
              in connection with a merger, acquisition, restructuring, or sale of assets, subject to
              this Policy or a materially similar successor policy.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                With your consent:
              </Box>{" "}
              for any other purpose we disclose to you at the time of collection, with your consent.
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          Other users can see: your username, profile, and any content you choose to post publicly
          (posts, comments, likes visible per Platform design). You control what you post — we don’t
          control what other users see beyond your own publishing choices.
        </Typography>
      </Box>

      {/* 7 */}
      <Box mb={4}>
        <Typography id="section-7" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          7. Cookies and Tracking Technologies
        </Typography>
        <Typography sx={styles.bodyText} mb={1}>
          We use cookies and similar technologies (such as local storage and pixels) to operate and
          improve the Platform. The categories we use are set out below.
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Strictly necessary
              </Box>{" "}
              — required for the Platform to function (login sessions, security, load balancing,
              fraud prevention). Examples: session tokens, CSRF protection tokens, authentication
              cookies. Consent required? No — cannot be disabled as they are essential. Typical
              duration: session, or up to 30 days for persistent login.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Functional
              </Box>{" "}
              — remember your preferences and settings. Examples: feed sort preference,
              theme/display settings, language preference. Consent required? Yes. Typical duration:
              up to 12 months.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Analytics
              </Box>{" "}
              {sectionLinks(
                "— understand how the Platform is used, in aggregate, to improve it. Examples: page views, feature usage, session duration. Consent required? Yes. Typical duration: up to 26 months (see Section 11)."
              )}
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Advertising
              </Box>{" "}
              — we do not currently run advertising cookies. If this changes, we will update this
              list and this Policy before deployment, and will provide the required opt-out/consent
              mechanisms.
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          {sectionLinks(
            "You can manage cookie preferences via your browser settings and, where provided, our in-product cookie preference centre. We also honour Global Privacy Control (GPC) signals — see Section 15.5."
          )}
        </Typography>
      </Box>

      {/* 8 */}
      <Box mb={4}>
        <Typography id="section-8" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          8. Overseas Data Transfers
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          Some of our service providers, including Speed (tryspeed.com) and certain cloud hosting
          and analytics providers, are based in or process data in the United States or other
          countries.
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          Under Australian Privacy Principle 8 (APP 8) and section 16C of the Privacy Act 1988,
          before we disclose your personal information to an overseas recipient, we take reasonable
          steps to ensure that recipient does not breach the APPs in relation to your information.
          Importantly, Wethemrkt remains accountable for your personal information even after it has
          been disclosed to an overseas recipient — if an overseas provider mishandles your data in
          a way that would breach the APPs, Wethemrkt may be held responsible under the Privacy Act
          as though we had handled it ourselves, except in limited circumstances specified by the
          Privacy Act (such as where you have consented after being expressly told APP 8.1 will not
          apply, or the overseas recipient is subject to a substantially similar privacy law with
          enforcement mechanisms you can access).
        </Typography>
        <Typography sx={styles.bodyText}>
          {sectionLinks(
            "Where we transfer personal information overseas, we do so under contractual protections with our service providers (such as Speed’s own certifications — see Section 9.3) designed to ensure your information continues to receive appropriate protection."
          )}
        </Typography>
      </Box>

      {/* 9 */}
      <Box mb={4}>
        <Typography id="section-9" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          9. Payments, Wallets, and Speed
        </Typography>

        <Typography sx={styles.listItemTitle}>9.1 Our Payments Backend</Typography>
        <Typography sx={styles.bodyText} mb={2} mt={1}>
          {sectionLinks(
            "Wethemrkt uses Speed (tryspeed.com) as our payments backend to process deposits, withdrawals, subscriptions, and the platform’s 10% transaction fee on likes, zaps, and paid content. When you deposit funds, withdraw funds, or transact on the Platform, certain information about you and your transactions is shared with Speed so that it can process the payment. Information shared with Speed may include: your account identifier, transaction amounts and timestamps, wallet addresses (Solana / Bitcoin Lightning), and — where the $1,000/month threshold is exceeded — identity verification information described in Section 10."
          )}
        </Typography>

        <Typography sx={styles.listItemTitle}>9.2 How Your Balance Is Held</Typography>
        <Typography sx={styles.bodyText} mb={1} mt={1}>
          All user balances on Wethemrkt are held in USDC, in an omnibus (pooled) wallet held in
          trust with Speed. This means your funds are not held in a segregated wallet under your
          name alone, but Speed maintains records identifying each user’s entitlement within the
          omnibus wallet. Deposits and withdrawals are available via:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>Solana — network fee of 1%</Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>Bitcoin Lightning — network fee of 2%</Typography>
          </ListItem>
        </List>

        <Typography
          id="section-9-3"
          sx={{ ...styles.listItemTitle, scrollMarginTop: "24px" }}
          mt={2}
        >
          9.3 Speed’s Certifications
        </Typography>
        <Typography sx={styles.bodyText} mb={1} mt={1}>
          Speed maintains the following certifications and licenses, which we note here for
          transparency (these are Speed’s certifications, not Wethemrkt’s, and are current as of the
          date Speed last confirmed them to us):
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>SOC 2 Type 2</Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>PCI-DSS (Level 1)</Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>ISO 27001</Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              FinCEN Money Services Business (MSB) registration #31000292053099, held via CoinX USA
              LLC
            </Typography>
          </ListItem>
        </List>

        <Typography sx={styles.listItemTitle} mt={2}>
          9.4 Our Role vs. Speed’s Role
        </Typography>
        <Typography sx={styles.bodyText} mt={1}>
          Wethemrkt is the platform where you interact, post, and transact. Speed is our payments
          infrastructure provider and is responsible for the custodial wallet infrastructure, KYC
          verification tooling (which we configure and rely on), and movement of funds on-chain. For
          questions about how Speed itself handles data as an independent business, Speed’s own
          privacy policy (available at tryspeed.com) also applies to the processing Speed performs.
        </Typography>
      </Box>

      {/* 10 */}
      <Box mb={4}>
        <Typography id="section-10" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          10. Identity Verification (KYC) and Anti-Money Laundering
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          To comply with anti-money laundering and counter-terrorism financing (AML/CTF) laws, and
          consistent with standard practice for financial services and payment platforms, Wethemrkt
          (via Speed) applies a $1,000 AUD per month combined deposit and withdrawal cap for users
          who have not completed identity verification.
        </Typography>
        <Typography sx={styles.bodyText} mb={1}>
          If you exceed this threshold, you will be required to complete Know Your Customer (KYC)
          verification before further deposits or withdrawals can be processed. KYC verification
          typically requires:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              A government-issued identity document (e.g., passport, driver’s licence, national ID).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Basic personal details to confirm your identity (name, date of birth, address).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              In some cases, a selfie or liveness check to match you to your identity document.
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          {sectionLinks(
            "KYC identity documents and verification data are Sensitive Personal Information (see Section 4) and are handled with heightened care. This data is collected and processed by Speed on our behalf, is used solely for identity verification and AML/CTF compliance purposes, and is retained per the schedule in Section 11."
          )}
        </Typography>
      </Box>

      {/* 11 */}
      <Box mb={4}>
        <Typography id="section-11" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          11. Data Retention
        </Typography>
        <Typography sx={styles.bodyText} mb={1}>
          We retain personal information only for as long as necessary for the purposes described in
          this Policy, or as required by law. Our retention schedule is as follows:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Account data:
              </Box>{" "}
              retained for the life of the account, plus 30 days after account deletion.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Financial transaction records:
              </Box>{" "}
              retained for 7 years (financial services recordkeeping standards).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                KYC / identity verification data:
              </Box>{" "}
              retained for 7 years after your last transaction (AML/CTF legal requirement).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Content posts:
              </Box>{" "}
              retained while active on the Platform; deleted 30 days after account closure.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Support tickets and correspondence:
              </Box>{" "}
              retained for 2 years.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Analytics data:
              </Box>{" "}
              retained for 26 months, in de-identified form.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Cookies:
              </Box>{" "}
              {sectionLinks("retained per cookie type — see the list in Section 7.")}
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          After the applicable retention period, we securely delete, de-identify, or anonymise the
          relevant data, except where longer retention is required by law (e.g., an active legal,
          tax, or regulatory obligation).
        </Typography>
      </Box>

      {/* 12 */}
      <Box mb={4}>
        <Typography id="section-12" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          12. Data Security and Breach Notification
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          {sectionLinks(
            "We take reasonable technical and organisational measures to protect personal information against unauthorised access, misuse, loss, or disclosure, including relying on the certified infrastructure of our payments provider, Speed (see Section 9.3). No method of transmission or storage is completely secure, and we cannot guarantee absolute security."
          )}
        </Typography>

        <Typography sx={styles.listItemTitle}>
          12.1 Our Commitment Under the Notifiable Data Breaches Scheme
        </Typography>
        <Typography sx={styles.bodyText} mb={1} mt={1}>
          Wethemrkt is committed to complying with the Notifiable Data Breaches (NDB) scheme under
          Part IIIC of the Privacy Act 1988. If we experience an eligible data breach — one that is
          likely to result in serious harm to any individual whose personal information is involved
          — we will:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Carry out a reasonable and expeditious assessment of the suspected breach.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Notify the Office of the Australian Information Commissioner (OAIC) as soon as
              practicable.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Notify affected individuals as soon as practicable, including recommended steps they
              can take in response.
            </Typography>
          </ListItem>
        </List>

        <Typography sx={styles.listItemTitle} mt={2}>
          12.2 Recent Reforms Context
        </Typography>
        <Typography sx={styles.bodyText} mt={1}>
          Following the 2024–2026 reforms to the Privacy Act 1988, penalties for serious or repeated
          interferences with privacy — and for tier-1 civil penalty contraventions — can reach the
          greater of AUD $50 million, three times the value of any benefit obtained, or 30% of
          adjusted turnover during the relevant period. We take our obligations under the Privacy
          Act seriously, and our security and incident response practices are designed with these
          obligations in mind.
        </Typography>
      </Box>

      {/* 13 */}
      <Box mb={4}>
        <Typography id="section-13" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          13. Your Privacy Rights — Australia
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          Wethemrkt handles personal information in accordance with the 13 Australian Privacy
          Principles (APPs) set out in Schedule 1 of the Privacy Act 1988 (Cth), as applicable law
          we comply with. These principles govern, among other things, how we collect, use,
          disclose, store, and give you access to your personal information, and how you can correct
          it.
        </Typography>

        <Typography sx={styles.listItemTitle}>13.1 Your Rights Under the APPs</Typography>
        <Typography sx={styles.bodyText} mb={1} mt={1}>
          Subject to certain exceptions permitted by law, you have the right to:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Access the personal information we hold about you (APP 12).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Correct personal information that is inaccurate, out of date, incomplete, irrelevant,
              or misleading (APP 13).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Know how your information is collected, used, and disclosed, and to whom (APPs 1, 3,
              6).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Interact with us anonymously or under a pseudonym where lawful and practicable (APP
              2).
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              {sectionLinks(
                "Complain about a suspected breach of the APPs, and have that complaint dealt with (see Section 13.3)."
              )}
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          We will respond to APP-based access and correction requests as soon as practicable.
          Because the Privacy Act does not prescribe a single fixed statutory deadline for all such
          requests, we aim to acknowledge requests promptly and resolve them without unreasonable
          delay, consistent with our obligations under APP 12 and APP 13.
        </Typography>

        <Typography sx={styles.listItemTitle} mt={2}>
          13.2 How to Exercise These Rights
        </Typography>
        <Typography sx={styles.bodyText} mt={1}>
          Email info@wethemrkt.com with your request. We may need to verify your identity before
          actioning certain requests, particularly those involving financial or KYC data.
        </Typography>

        <Typography
          id="section-13-3"
          sx={{ ...styles.listItemTitle, scrollMarginTop: "24px" }}
          mt={2}
        >
          13.3 How to Complain
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Contact us first at info@wethemrkt.com — we will investigate and aim to respond as
              soon as practicable.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Escalate to the OAIC (Office of the Australian Information Commissioner, oaic.gov.au)
              if you are not satisfied with our response, or at any time you consider appropriate.
              The OAIC can investigate complaints and has powers under the Privacy Act to make
              determinations, including in relation to serious or repeated breaches.
            </Typography>
          </ListItem>
        </List>
      </Box>

      {/* 14 */}
      <Box mb={4}>
        <Typography id="section-14" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          14. Automated Decision-Making
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          (Placeholder section — to be expanded ahead of the December 2026 Australian automated
          decision-making transparency reforms.)
        </Typography>
        <Typography sx={styles.bodyText} mb={1}>
          Wethemrkt does not currently use algorithmic feeds, and feed and trending sort order are
          user-controlled rather than automated. We do not currently use automated decision-making
          (ADM) or automated decision-making technology (ADMT) that significantly affects your
          rights or interests. If this changes — for example, through the introduction of automated
          risk-scoring, automated eligibility decisions for features such as Alpha Awards, or any
          other automated process that could significantly affect you — we will update this Policy
          in advance to disclose:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>The categories of decisions involved.</Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              The types of personal information used in making them.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              How you can seek information about, and where applicable seek review of, a decision
              that affects you.
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          This disclosure is provided ahead of the automated decision-making transparency
          requirements expected to commence under Australian privacy law reforms in December 2026,
          and will be updated to reflect the final form of those requirements.
        </Typography>
      </Box>

      {/* 15 */}
      <Box mb={4}>
        <Typography id="section-15" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          15. Your Privacy Rights — United States
        </Typography>
        <Typography sx={styles.bodyText} mb={2}>
          If you are a resident of certain US states, you have specific rights under applicable
          state privacy laws, including the California Consumer Privacy Act as amended by the
          California Privacy Rights Act (CCPA/CPRA), the Virginia Consumer Data Protection Act
          (VCDPA), the Colorado Privacy Act (CPA), and other US state privacy laws such as the Utah
          Consumer Privacy Act (UCPA), the Connecticut Data Privacy Act (CTDPA), and the Texas Data
          Privacy and Security Act (TDPSA). While these laws differ in detail, they generally
          provide the following rights, which we honour for residents of applicable states:
        </Typography>

        <Typography sx={styles.listItemTitle}>15.1 Right to Know / Access</Typography>
        <Typography sx={styles.bodyText} mb={2} mt={1}>
          You have the right to request that we disclose the personal information we have collected
          about you, including the categories of information, sources, purposes of processing, and
          categories of third parties with whom we share it.
        </Typography>

        <Typography sx={styles.listItemTitle}>15.2 Right to Delete</Typography>
        <Typography sx={styles.bodyText} mb={2} mt={1}>
          {sectionLinks(
            "You have the right to request deletion of personal information we have collected from you, subject to certain exceptions (e.g., information we must retain for legal, security, or financial recordkeeping purposes as described in Section 11)."
          )}
        </Typography>

        <Typography sx={styles.listItemTitle}>15.3 Right to Correct</Typography>
        <Typography sx={styles.bodyText} mb={2} mt={1}>
          You have the right to request correction of inaccurate personal information we maintain
          about you.
        </Typography>

        <Typography id="section-15-4" sx={{ ...styles.listItemTitle, scrollMarginTop: "24px" }}>
          15.4 Right to Limit Use of Sensitive Personal Information
        </Typography>
        <Typography sx={styles.bodyText} mb={2} mt={1}>
          {sectionLinks(
            "Under the CPRA, you have the right to direct us to limit our use of your Sensitive Personal Information (see Section 4) to only what is necessary to perform the services you have requested. As noted above, we already limit our use of SPI in this way by default; you may still submit a request to confirm or reinforce this."
          )}
        </Typography>

        <Typography id="section-15-5" sx={{ ...styles.listItemTitle, scrollMarginTop: "24px" }}>
          15.5 Right to Opt Out of Sale or Sharing / Global Privacy Control
        </Typography>
        <Typography sx={styles.bodyText} mb={2} mt={1}>
          You have the right to opt out of the “sale” or “sharing” (as those terms are defined under
          CPRA, including for cross-context behavioural advertising) of your personal information.
          Wethemrkt does not sell personal information for monetary consideration and does not share
          personal information for cross-context behavioural advertising. To the extent any future
          practice could be considered a “sale” or “share” under applicable law, you can opt out via
          our “Do Not Sell or Share My Personal Information” link. We also recognise and will honour
          Global Privacy Control (GPC) signals sent by your browser or browser extension as a valid
          request to opt out of sale/sharing under CCPA/CPRA and similar state laws, without
          requiring you to take additional action.
        </Typography>

        <Typography sx={styles.listItemTitle}>15.6 Right to Non-Discrimination</Typography>
        <Typography sx={styles.bodyText} mb={2} mt={1}>
          We will not discriminate against you for exercising any of these rights, including by
          denying services, charging different prices, or providing a different level or quality of
          service (except as permitted by law, e.g., financially reasonable differences related to
          the value provided by your data).
        </Typography>

        <Typography sx={styles.listItemTitle}>15.7 Right to Appeal</Typography>
        <Typography sx={styles.bodyText} mb={2} mt={1}>
          Some states (e.g., Colorado, Virginia) provide a right to appeal our decision on your
          request. If you wish to appeal, contact us at info@wethemrkt.com and we will provide
          information on the applicable appeal process for your state.
        </Typography>

        <Typography sx={styles.listItemTitle}>15.8 How We Respond — Timelines</Typography>
        <Typography sx={styles.bodyText} mb={1} mt={1}>
          We take the following timelines seriously and have built our request-handling process
          around them:
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Any request (initial acknowledgment): acknowledged within 10 business days. No
              separate response deadline applies to acknowledgment itself, and no extension applies
              to this step.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Right to Know / Access: acknowledged within 10 business days; response deadline of 45
              calendar days from receipt. Extension available — once, by an additional 45 days (90
              days total), with notice within the initial 45-day period.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Right to Delete / Right to Correct: acknowledged within 10 business days; response
              deadline of 45 calendar days from receipt. Extension available — same as above.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Right to Opt Out of Sale/Sharing: acknowledged within 10 business days; response
              deadline of 15 business days from receipt. Not extendable.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              Right to Limit Use of Sensitive Personal Information: acknowledged within 10 business
              days; response deadline of 15 business days from receipt. Not extendable.
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          We will verify your identity before completing requests to know, access, delete, or
          correct, using information proportionate to the sensitivity of the data involved (this may
          include additional verification for requests touching financial or KYC data).
        </Typography>

        <Typography sx={styles.listItemTitle} mt={2}>
          15.9 How to Submit a Request
        </Typography>
        <Typography sx={styles.bodyText} mt={1}>
          Email info@wethemrkt.com with the subject line “Privacy Rights Request,” specifying which
          right you are exercising and your state of residence. You may also use an authorised agent
          to submit a request on your behalf, subject to verification.
        </Typography>

        <Typography sx={styles.listItemTitle} mt={2}>
          15.10 COPPA and FTC Compliance
        </Typography>
        <Typography sx={styles.bodyText} mt={1}>
          {sectionLinks(
            "We comply with the Children’s Online Privacy Protection Act (COPPA) and follow Federal Trade Commission (FTC) guidance on privacy and data practices, including transparency about data collection and use, and reasonable security practices. See Section 16 for our children’s privacy commitments."
          )}
        </Typography>
      </Box>

      {/* 16 */}
      <Box mb={4}>
        <Typography id="section-16" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          16. Children’s Privacy
        </Typography>
        <Typography sx={styles.bodyText} mb={1}>
          Wethemrkt is not directed to, and is not intended for use by, children. You must be at
          least 13 years old to use the Platform (and at least 18, or the age of majority in your
          jurisdiction, to transact financially on the Platform, given its trading and investment
          discussion focus).
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                United States:
              </Box>{" "}
              We comply with the Children’s Online Privacy Protection Act (COPPA) and do not
              knowingly collect personal information from children under 13. If we become aware that
              we have collected personal information from a child under 13 without verifiable
              parental consent, we will delete that information promptly.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Additional obligations:
              </Box>{" "}
              We design our data practices with reference to Australia’s Children’s Online Privacy
              Code, expected to commence in December 2026, as applicable law we comply with, and
              will update our practices and this Policy as needed to align with its final
              requirements once in force.
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          If you believe a child has provided us with personal information, please contact us at
          info@wethemrkt.com so we can investigate and take appropriate action.
        </Typography>
      </Box>

      {/* 17 */}
      <Box mb={4}>
        <Typography id="section-17" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          17. Version History
        </Typography>
        <Typography sx={styles.bodyText}>
          Version 1.0 — 13 July 2026 — Initial comprehensive Privacy Policy drafted, covering
          Privacy Act/APP obligations, US state privacy laws (CCPA/CPRA, VCDPA, CPA, and others),
          Sensitive Personal Information, Speed/wallet data handling, KYC/AML, retention schedule,
          cookies, and children’s privacy.
        </Typography>
      </Box>

      {/* 18 */}
      <Box mb={4}>
        <Typography id="section-18" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          18. Changes to This Policy
        </Typography>
        <Typography sx={styles.bodyText}>
          We may update this Privacy Policy from time to time to reflect changes in our practices,
          our services, or legal requirements. If we make material changes, we will notify you by
          posting a notice on the Platform and/or by email, and by updating the “Last updated” date
          at the top of this Policy. We encourage you to review this Policy periodically.
        </Typography>
      </Box>

      {/* 19 */}
      <Box mb={4}>
        <Typography id="section-19" sx={{ ...styles.sectionTitle, scrollMarginTop: "24px" }}>
          19. Contact Us and Complaints
        </Typography>
        <Typography sx={styles.bodyText} mb={1}>
          We’ve tried to make this simple: one email for everything.
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Contact:
              </Box>{" "}
              info@wethemrkt.com — for privacy requests, rights, complaints, and general enquiries.
            </Typography>
          </ListItem>
          <ListItem sx={styles.listItem}>
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={styles.highlightedText}>
                Registered business address:
              </Box>{" "}
              [ADDRESS PLACEHOLDER]
            </Typography>
          </ListItem>
        </List>
        <Typography sx={styles.bodyText} mt={2}>
          <Box component="span" sx={styles.highlightedText}>
            Users covered by Australian privacy law:
          </Box>{" "}
          If you are not satisfied with our response to a privacy complaint, you may refer your
          complaint to the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au.
        </Typography>
        <Typography sx={styles.bodyText} mt={1}>
          <Box component="span" sx={styles.highlightedText}>
            US users:
          </Box>{" "}
          If you have concerns about how we’ve handled a request under CCPA/CPRA, VCDPA, CPA, or
          another applicable state law, you may contact your state Attorney General’s office or, in
          California, the California Privacy Protection Agency, in addition to contacting us
          directly.
        </Typography>
      </Box>

      <Box mb={2}>
        <Typography sx={styles.bodyText} fontStyle="italic">
          This Privacy Policy was drafted based on Wethemrkt’s stated business practices as of July
          2026, is intended as a template for legal review, and does not constitute legal advice.
          Please have it reviewed by qualified legal counsel before publication, and revisit it
          whenever Wethemrkt’s data practices, vendors, or the applicable law change.
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
